import { useCallback } from 'react';
import useCurrentAccount from '../useCurrentAccount';
import {
  useGetWidgetSettingsQuery,
  useUpdateWidgetSettingsMutation,
} from '~/graphql/types';
import formatToastMessage from '~/util/formatToastMessage';
import useAddToast from '../useAddToast';
import Catalog from '~/Catalog';
import useTrackEvent from '../useTrackEvent';
import getImageInput from '~/util/getImageInput';
import { reporter } from '../useErrorReporter';

const text = {
  successMsg: 'Slimme weergave is ingeschakeld',
};

const useContextAwareToggle = () => {
  const trackEvent = useTrackEvent();
  const account = useCurrentAccount();
  const addToast = useAddToast();
  const { data, loading, updateQuery } = useGetWidgetSettingsQuery({
    variables: {
      accountId: account.id,
    },
  });
  const [updateWidgetSettings, { loading: updateLoading }] =
    useUpdateWidgetSettingsMutation();

  const onChange = useCallback(async (value: boolean) => {
    const widgetSettings = data?.getWidgetSettings;

    if (!widgetSettings) {
      reporter.captureException(
        new Error('Widget settings not found in useContextAwareToggle'),
        'error',
      );
      return addToast([
        formatToastMessage(Catalog.genericUnknownErrorMessageShort, 'danger'),
      ]);
    }

    const { logo, highlightImage } = widgetSettings;

    void updateWidgetSettings({
      variables: {
        accountId: account.id,
        widgetSettings: {
          highlightImage: getImageInput(highlightImage) || null,
          logo: logo?.s3key ? { s3key: logo.s3key } : null,
          contextAware: value,
        },
      },
    }).then(({ data, errors }) => {
      if (errors && errors.length > 0) {
        addToast([
          formatToastMessage(Catalog.genericUnknownErrorMessageShort, 'danger'),
        ]);
        return;
      }

      if (data) {
        updateQuery(prev => ({
          ...prev,
          getWidgetSettings: data.updateWidgetSettings,
        }));

        addToast([formatToastMessage(text.successMsg, 'success')]);

        trackEvent({
          eventName: value ? 'context_aware_enabled' : 'context_aware_disabled',
          parameters: {
            context_aware: value,
          },
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    value: data?.getWidgetSettings?.contextAware || false,
    queryLoading: loading,
    updateLoading: updateLoading,
    onChange,
  };
};

export default useContextAwareToggle;
